import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { ImageIcon, X } from "lucide-react";
import { useState } from "react";
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import { type MediaItem, MediaSelectionModal } from "./media-selection-modal";

interface MediaSelectorProps<T extends FieldValues> {
  field: ControllerRenderProps<T, Path<T>>;
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export function MediaSelector<T extends FieldValues>({
  field,
  label,
  description,
  disabled = false,
  className,
  placeholder = "No media selected",
}: MediaSelectorProps<T>) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);

  const handleMediaSelect = (media: MediaItem) => {
    setSelectedMedia(media);
    // Store the media ID in the form field
    field.onChange(media.id);
  };

  const handleRemove = () => {
    setSelectedMedia(null);
    field.onChange("");
  };

  const MediaPreview = () => {
    if (!selectedMedia) return null;

    const isImage =
      selectedMedia.cloudStorageObject.mimeType?.startsWith("image/");

    return (
      <div className="mt-3 p-3 border rounded-lg bg-muted/30">
        <div className="flex items-center gap-3">
          {isImage ? (
            <img
              src={selectedMedia.cloudStorageObject.fullUrl}
              alt={selectedMedia.title}
              className="w-16 h-16 object-cover rounded"
            />
          ) : (
            <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">
              {selectedMedia.title}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {selectedMedia.description}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {selectedMedia.cloudStorageObject.mimeType
                ?.split("/")[1]
                ?.toUpperCase() || "FILE"}
            </p>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleRemove}
            disabled={disabled}
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <FormItem className={className}>
      {label && <FormLabel>{label}</FormLabel>}
      <FormControl>
        <div>
          <Button
            type="button"
            variant="outline"
            className={cn(
              "w-full h-auto min-h-[60px] justify-start",
              !selectedMedia && "border-dashed hover:bg-muted/50",
            )}
            onClick={() => setModalOpen(true)}
            disabled={disabled}
          >
            <div className="flex items-center gap-3 w-full">
              <div className="shrink-0">
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-left">
                <p className="font-medium">
                  {selectedMedia ? "Change Media" : "Select Media"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedMedia ? selectedMedia.title : placeholder}
                </p>
              </div>
            </div>
          </Button>

          <MediaPreview />
        </div>
      </FormControl>

      {description && (
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      )}

      <FormMessage />

      <MediaSelectionModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSelect={handleMediaSelect}
        selectedMediaId={field.value}
        title="Select Media"
      />
    </FormItem>
  );
}
